import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Loader2, Save, Plus, X, GripVertical, Globe, Mail, CheckCircle2, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';

const SETTING_LABELS = {
  quote_number_prefix: { label: 'Quote Number Prefix', desc: 'Prefix for new quotes (e.g. C4P-QUO). New quotes will be numbered C4P-QUO-00001, 00002, ...' },
  quote_number_counter: { label: 'Quote Counter (last used)', desc: 'Sequential counter for quote numbers. Read-only - auto-increments on each new quote.' },
  contract_number_prefix: { label: 'Contract Number Prefix', desc: 'Prefix for contract numbers (e.g. C4P-CON). Contracts are created automatically when a quote is signed.' },
  contract_number_counter: { label: 'Contract Counter (last used)', desc: 'Sequential counter for contract numbers. Read-only - auto-increments when a signed quote becomes a contract.' },
  quote_counter_year: { label: 'Legacy Quote Counter Year', desc: 'Year for the old-format sequential counter (legacy, pre-C4P-QUO format).' },
  quote_counter_value: { label: 'Legacy Quote Counter Value', desc: 'Current sequential counter for old-format quotes (legacy).' },
  default_currency: { label: 'Default Currency', desc: 'Default currency for new quotes (GBP, EUR, USD, CAD, AUD)' },
  default_term_months: { label: 'Default Term (months)', desc: 'Default quote term length in months' },
  company_name: { label: 'Company Name', desc: 'Used in PDF quote output' },
  audit_log_retention_days: { label: 'Audit Log Retention (days)', desc: 'Entries older than this many days are eligible for purging via the "Purge Old Logs" button on the Audit Log page. Default: 365 days (1 year). Minimum recommended: 90 days. Not automatic — an admin must trigger the purge.' },
  company_address: { label: 'Company Address', desc: 'Used in PDF quote output' },
  company_logo_url: { label: 'Company Logo URL', desc: 'URL for company logo (used in PDF and branding)' },
};

const TABS = [
  { id: 'general',     label: 'General' },
  { id: 'app-servers', label: 'App Server Purposes' },
  { id: 'countries',   label: 'Countries' },
];

export default function SettingsAdmin() {
  const [activeTab, setActiveTab] = useState('general');

  // General settings
  const [settings, setSettings] = useState([]);
  const [values, setValues]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});

  // SMTP test
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpResult, setSmtpResult]   = useState(null);

  // Signing email toggle
  const [signingEmailEnabled, setSigningEmailEnabled] = useState(true);
  const [signingToggleSaving, setSigningToggleSaving] = useState(false);

  // App Server Purposes
  const [purposes, setPurposes]               = useState([]);
  const [purposesLoading, setPurposesLoading] = useState(true);
  const [purposesSaving, setPurposesSaving]   = useState(false);
  const [newPurpose, setNewPurpose]           = useState('');

  // Countries
  const [countries, setCountries]               = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesSaving, setCountriesSaving]   = useState(false);
  const [newCountry, setNewCountry]             = useState('');

  useEffect(() => {
    adminAPI.getSettings()
      .then(({ data }) => { setSettings(data || []); const v = {}; (data || []).forEach(s => { v[s.key] = s.value; }); setValues(v); })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
    adminAPI.getAppServerPurposes()
      .then(({ data }) => setPurposes(data.purposes || []))
      .catch(() => toast.error('Failed to load app server purposes'))
      .finally(() => setPurposesLoading(false));
    adminAPI.getCountries()
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => toast.error('Failed to load countries'))
      .finally(() => setCountriesLoading(false));
    adminAPI.getSigningEmailEnabled()
      .then(({ data }) => setSigningEmailEnabled(data.enabled))
      .catch(() => {});
  }, []);

  const save = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try { await adminAPI.updateSetting(key, values[key]); toast.success(`'${SETTING_LABELS[key]?.label || key}' saved`); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true); setSmtpResult(null);
    try {
      const { data } = await adminAPI.testSmtp();
      setSmtpResult(data);
      if (data.ok) toast.success(`Test email sent to ${data.sent_to}`);
      else toast.error(`SMTP test failed: ${data.error}`);
    } catch (e) { toast.error('SMTP test request failed'); }
    finally { setSmtpTesting(false); }
  };

  const toggleSigningEmail = async (val) => {
    setSigningToggleSaving(true);
    try {
      await adminAPI.setSigningEmailEnabled(val);
      setSigningEmailEnabled(val);
      toast.success(val ? 'Signing link emails enabled' : 'Signing link emails disabled');
    } catch { toast.error('Failed to update setting'); }
    finally { setSigningToggleSaving(false); }
  };

  const addPurpose = () => {
    const t = newPurpose.trim();
    if (!t) return;
    if (purposes.includes(t)) { toast.error('Purpose already exists'); return; }
    setPurposes(p => [...p, t].sort((a, b) => a.localeCompare(b)));
    setNewPurpose('');
  };
  const removePurpose = (idx) => setPurposes(p => p.filter((_, i) => i !== idx));
  const sortPurposes = () => setPurposes(p => [...p].sort((a, b) => a.localeCompare(b)));
  const movePurpose = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setPurposes(p => {
      const next = [...p];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  };
  const savePurposes = async () => {
    setPurposesSaving(true);
    try { await adminAPI.saveAppServerPurposes(purposes); toast.success('App server purposes saved'); }
    catch { toast.error('Failed to save purposes'); }
    finally { setPurposesSaving(false); }
  };

  const addCountry = () => {
    const t = newCountry.trim();
    if (!t) return;
    if (countries.includes(t)) { toast.error('Country already in list'); return; }
    setCountries(c => {
      const without = c.filter(x => x !== 'Other' && x !== t);
      const others = c.includes('Other') ? ['Other'] : [];
      const inserted = [...without, t].sort((a, b) => a.localeCompare(b));
      return [...inserted, ...others];
    });
    setNewCountry('');
  };
  const removeCountry = (idx) => setCountries(c => c.filter((_, i) => i !== idx));
  const sortCountries = () => setCountries(c => {
    const others = c.filter(x => x === 'Other');
    const rest = c.filter(x => x !== 'Other').sort((a, b) => a.localeCompare(b));
    return [...rest, ...others];
  });
  const saveCountries = async () => {
    setCountriesSaving(true);
    try {
      await adminAPI.saveCountries(countries);
      try { const { _bustCountryCache } = await import('../../components/CountrySelect'); _bustCountryCache(); } catch {}
      toast.success('Countries saved');
    } catch { toast.error('Failed to save countries'); }
    finally { setCountriesSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-settings-page">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>System Settings</h2>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>Configure quote format, default values, company branding, and reference data.</p>
      </div>

      {/* Segmented pill tabs */}
      <div className="flex items-center gap-0.5 p-1 rounded-xl w-fit" style={{ background: '#e2e8f0' }}>
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            data-testid={`settings-tab-${id}`}
            className="px-4 py-1.5 text-sm rounded-lg transition-all duration-150 outline-none"
            style={{
              color: activeTab === id ? NAVY : '#64748b',
              fontWeight: activeTab === id ? 600 : 400,
              background: activeTab === id ? 'white' : 'transparent',
              boxShadow: activeTab === id ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content card */}
      <div className="rounded-xl border" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div className="p-6" style={{ background: '#f8fafc' }}>

          {/* ── General ── */}
          {activeTab === 'general' && (
            <div className="space-y-4 max-w-xl">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                Core system values for quote numbering, defaults, and company branding used in PDF output.
              </p>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
                : settings.map(s => {
                    const meta = SETTING_LABELS[s.key] || { label: s.key, desc: s.description };
                    const isReadOnly = s.key === 'quote_number_counter' || s.key === 'contract_number_counter' || s.key === 'quote_counter_year' || s.key === 'quote_counter_value';
                    return (
                      <div key={s.key} className="rounded-xl border bg-white p-4 space-y-2">
                        <div>
                          <Label className="text-sm font-semibold">{meta.label}</Label>
                          <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{meta.desc}</p>
                        </div>
                        <div className="flex gap-3">
                          <Input
                            value={values[s.key] ?? ''}
                            onChange={e => !isReadOnly && setValues(v => ({ ...v, [s.key]: e.target.value }))}
                            className="flex-1"
                            readOnly={isReadOnly}
                            style={isReadOnly ? { background: '#f8fafc', color: '#94a3b8', cursor: 'default' } : {}}
                            data-testid={`setting-${s.key}`}
                          />
                          {!isReadOnly && (
                            <Button onClick={() => save(s.key)} disabled={saving[s.key]} variant="outline" size="sm" className="gap-1">
                              {saving[s.key] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save size={13} />} Save
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
              }

              {/* Signing email toggle */}
              <div className="rounded-xl border bg-white p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm font-semibold">Signing Link - Auto-Email</Label>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                      When disabled, PSMs cannot auto-email signing links. Admins and managers are unaffected.
                      PSMs can still generate a link to copy and share manually.
                    </p>
                  </div>
                  <Switch
                    checked={signingEmailEnabled}
                    onCheckedChange={toggleSigningEmail}
                    disabled={signingToggleSaving}
                    data-testid="signing-email-toggle"
                  />
                </div>
                {!signingEmailEnabled && (
                  <div className="rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5"
                    style={{ background: '#fefce8', color: '#92400e', border: '1px solid #fde68a' }}>
                    <AlertCircle size={12} /> Email sending is currently disabled - PSMs cannot auto-email signing links.
                  </div>
                )}
              </div>

              {/* SMTP health check */}
              <div className="rounded-xl border bg-white p-4 space-y-3">
                <div>
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><Mail size={13} /> Email Delivery (SMTP)</Label>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                    Send a test email to confirm signing link emails are working. Check backend logs if the test fails.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={testSmtp} disabled={smtpTesting} variant="outline" size="sm" className="gap-1.5" data-testid="smtp-test-btn">
                    {smtpTesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail size={13} />}
                    {smtpTesting ? 'Sending…' : 'Send Test Email'}
                  </Button>
                  {smtpResult && (
                    <span className="flex items-center gap-1.5 text-xs font-medium"
                      style={{ color: smtpResult.ok ? '#15803d' : 'hsl(var(--danger))' }}>
                      {smtpResult.ok
                        ? <><CheckCircle2 size={13} /> Sent to {smtpResult.sent_to}</>
                        : <><AlertCircle size={13} /> {smtpResult.error}</>
                      }
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── App Server Purposes ── */}
          {activeTab === 'app-servers' && (
            <div className="max-w-xl space-y-4">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                Predefined purpose options shown when configuring application servers in a quote.
                PSMs can also type a custom purpose by selecting "Other".
              </p>
              <div className="rounded-xl border bg-white p-4 space-y-3">
                {purposesLoading ? (
                  <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>
                        {purposes.length} {purposes.length === 1 ? 'purpose' : 'purposes'}
                      </span>
                      <Button variant="outline" size="sm" onClick={sortPurposes}
                        className="gap-1 text-xs h-7" data-testid="sort-purposes-btn"
                        disabled={purposes.length < 2}>
                        A &rarr; Z
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      {purposes.map((p, i) => (
                        <div key={i}
                          draggable
                          onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.style.opacity = '0.6'; }}
                          onDragLeave={e => { e.currentTarget.style.opacity = '1'; }}
                          onDrop={e => { e.preventDefault(); e.currentTarget.style.opacity = '1'; const from = parseInt(e.dataTransfer.getData('text/plain'), 10); movePurpose(from, i); }}
                          onDragEnd={e => { e.currentTarget.style.opacity = '1'; }}
                          className="flex items-center gap-2 rounded-lg px-3 py-1.5 select-none"
                          style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', cursor: 'grab' }}>
                          <GripVertical size={13} style={{ color: 'hsl(var(--muted-fg))', flexShrink: 0 }} />
                          <span className="flex-1 text-sm">{p}</span>
                          <button onClick={() => removePurpose(i)}
                            className="p-0.5 rounded hover:bg-red-100 transition-colors"
                            style={{ color: 'hsl(var(--danger))' }}
                            data-testid={`remove-purpose-${i}`}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {purposes.length === 0 && (
                        <p className="text-sm text-center py-3" style={{ color: 'hsl(var(--muted-fg))' }}>No purposes defined - add some below.</p>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                      <Input placeholder="e.g. Customer Portal Server" value={newPurpose}
                        onChange={e => setNewPurpose(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addPurpose()}
                        className="flex-1 text-sm" data-testid="new-purpose-input" />
                      <Button onClick={addPurpose} disabled={!newPurpose.trim()} variant="outline" size="sm" className="gap-1">
                        <Plus size={13} /> Add
                      </Button>
                    </div>
                    <Button onClick={savePurposes} disabled={purposesSaving} className="w-full gap-2"
                      style={{ background: NAVY, color: 'white' }} data-testid="save-purposes-btn">
                      {purposesSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                      Save App Server Purposes
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Countries ── */}
          {activeTab === 'countries' && (
            <div className="max-w-xl space-y-4">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                Countries available in the partner and tenant country dropdowns. PSMs can search by typing.
              </p>
              <div className="rounded-xl border bg-white p-4 space-y-3">
                {countriesLoading ? (
                  <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>
                        {countries.length} {countries.length === 1 ? 'country' : 'countries'}
                      </span>
                      <Button variant="outline" size="sm" onClick={sortCountries}
                        className="gap-1 text-xs h-7" data-testid="sort-countries-btn"
                        disabled={countries.length < 2}>
                        A &rarr; Z
                      </Button>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                      {countries.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-lg px-3 py-1.5"
                          style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
                          <GripVertical size={13} style={{ color: 'hsl(var(--muted-fg))', flexShrink: 0 }} />
                          <span className="flex-1 text-sm">{c}</span>
                          <button onClick={() => removeCountry(i)}
                            className="p-0.5 rounded hover:bg-red-100 transition-colors"
                            style={{ color: 'hsl(var(--danger))' }}
                            data-testid={`remove-country-${i}`}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {countries.length === 0 && (
                        <p className="text-sm text-center py-3" style={{ color: 'hsl(var(--muted-fg))' }}>No countries defined - add some below.</p>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                      <Input placeholder="e.g. New Zealand" value={newCountry}
                        onChange={e => setNewCountry(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addCountry()}
                        className="flex-1 text-sm" data-testid="new-country-input" />
                      <Button onClick={addCountry} disabled={!newCountry.trim()} variant="outline" size="sm" className="gap-1">
                        <Plus size={13} /> Add
                      </Button>
                    </div>
                    <Button onClick={saveCountries} disabled={countriesSaving} className="w-full gap-2"
                      style={{ background: NAVY, color: 'white' }} data-testid="save-countries-btn">
                      {countriesSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                      Save Countries
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
